import { GenericState, GenericBesuState, ISODate, UploadedGCSFile, HistoryItem, UUID } from "./common";
import { InvoiceStatus, InvoiceStatusWithPaymentInfo, MOCK_INVOICE_LIST } from "./invoice";
import { PaymentOrderStatus, PaymentStatus } from "./payment";

type ClaimProps = {
    referenceId: UUID;
    projectReferenceId: UUID;
    descriptionRef: string;
    approverCount: number;
    invoiceCount: number;
    documents: UploadedGCSFile[];
    documentCount: number;
    approvers: BesuClaimApproversPersonalInfo[];
    claimApprovers: BesuClaimApprovers[];
    invoiceReferenceIds: UUID[];
    contractor: BesuParty;
    contractorWallet: string;
    developer: BesuParty;
    developerWallet: string;
    commentRecords: CommentRecord[];
};

export type BesuClaimListItem = {
    referenceId: UUID;
    projectReferenceId: UUID;
    createdAt: ISODate;
    updatedAt: ISODate;
    claimStatus: ClaimStatus;
    contractorWallet: string;
    contractorAccountName: string;
    contractorDisplayName: string;
    invoiceCount: number;
    claimApprovers: BesuClaimListApprover[];
    invoiceStatuses: BesuClaimListInvoiceStatus[];
    contractor: BesuParty;
    developer: BesuParty;
    commentRecords: CommentRecord[];
};

export type BesuClaimState = GenericBesuState<ClaimProps, ClaimStatus>;

export type ClaimState = GenericState<ClaimProps, ClaimStatus>;

export enum ClaimStatus {
    NONE = "NONE",
    SUBMITTED = "SUBMITTED", // CONTRACTOR_SUBMITTED
    RESUBMITTED = "RESUBMITTED",
    PARTIAL_CA_APPROVED = "PARTIAL_CA_APPROVED",
    ALL_CA_APPROVED = "ALL_CA_APPROVED",
    APPROVED = "APPROVED", // PO_APPROVED
    CA_REJECTED = "CA_REJECTED",
    PO_REJECTED = "PO_REJECTED",
    DISCARDED = "DISCARDED", // CONTRACTOR_DISCARDED
    DELETED = "DELETED",
}

export type ClaimStateWithNestedStatus = {
    claimState: ClaimState;
    invoiceStatuses: InvoiceStatusWithPaymentInfo[];
};

export type ClaimStatusWithNestedStatus = {
    claimStatus: ClaimStatus;
    invoiceStatuses: InvoiceStatusWithPaymentInfo[];
};

export enum ApproveStatus {
    PENDING = "PENDING",
    APPROVED = "APPROVED",
    REJECTED = "REJECTED",
    INVALID = "INVALID",
}

export type ApproverDetailsPersonalInfo = {
    roleName: string;
    firstName: string;
    lastName: string;
    party?: AccountInfo;
    email: string;
    userProfileName: string;
};

export type AccountInfo = {
    account: {
        name: string;
        host: string;
        identifier: AccountInfoId;
        linearId: AccountInfoId;
        participants: string[];
    },
    party: string;
}

export type AccountInfoId = {
    externalId?: string;
    id: string;
}

export type ApproverDetails = {
    status?: ApproveStatus;
    actionTimestamp?: ISODate | null;
    details: ApproverDetailsPersonalInfo;
};

export type BesuClaimListStatus = ClaimStatus | "DELETED";


export type BesuApproveStatus = ApproveStatus | "DISCARDED";

export type BesuClaimListApprover = Omit<ApproverDetails, "status"> & {
    status?: BesuApproveStatus;
};

export type BesuClaimListPaymentInfo = {
    paymentOrderStatus: PaymentOrderStatus;
    paymentStatus: PaymentStatus | null;
    paymentOrderApprovers: BesuClaimListApprover[];
    referenceId: UUID;
};

export type BesuClaimListInvoiceStatus = {
    invoiceStatus: InvoiceStatus;
    paymentInfo: BesuClaimListPaymentInfo | null;
    referenceId: UUID;
};

export type ClaimHistory = {
    referenceId: string;
    currentStatus: ClaimStatus;
    createdAt: ISODate;
    updatedAt: ISODate;
    statusHistory: HistoryItem<ClaimStatus>[];
};


export type BesuClaimApproversPersonalInfo = {
    wallet: string;
    userHashHex: string;
    roleName: string;
    externalRef: string;
    status: number;
    actionTimestamp: ISODate;
};

export type BesuClaimApprovers = {
    status?: BesuApproveStatus;
    actionTimestamp?: ISODate | null;
    details: ApproverDetailsPersonalInfo;
};

export type BesuParty = {
    accountName: string;
    userProfileName: string;
    wallet: string;
};

export type CommentRecord = {
    authorHash: string;
    commentRef: string;
    email: string;
    fullName: string;
    timestamp: ISODate;
    resolved: boolean;
};

const MOCK_CLAIM_PROPS: ClaimProps = {
    referenceId: "b602f3e5-bc5a-86e3-a6e0-17f7f869c2ba",
    projectReferenceId: "b602f3e5-ffff-86e3-xxxx-17f7f869c2ba",
    contractorWallet: "0x07f26d0b5d085ea964eed0f8dfac438f2bd1c9f4",
    developerWallet: "0x628d684197485c054cda7d3def46e8be6b3d174c",
    descriptionRef: "VT12cc",
    documentCount: 1,
    approverCount: 3,
    invoiceCount: 0,
    documents: [
        {
            documentId: "invoice.pdf",
            documentName: "invoice.pdf",
            documentHashHex: "0x9251fc2985832f251ec23ba60efe06b474cb7b74ee844686db1b56c9384f4eb0"
        }
    ],
    approvers: [
        {
            wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c",
            userHashHex: "0x0e5f9d27cd5b71f3bf06a70750958de5885e55e855457fc31af5be47a26fc121",
            roleName: "123213",
            externalRef: "Approver Entity",
            status: 0,
            actionTimestamp: "2026-07-08T07:51:39Z"
        },
        {
            wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c",
            userHashHex: "0xf09b66dfb6bd1bb5e7d2be0b15a80542e02b79b94ea63cd7e918ac65b1164a9a",
            roleName: "123213",
            externalRef: "Approver Entity",
            status: 0,
            actionTimestamp: "2026-07-08T07:51:39Z"
        },
        {
            wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c",
            userHashHex: "0x61533c4c2e198353cde1c7df7a23852535a93a5d1f2ee39863bb3cf118855a53",
            roleName: "1232131",
            externalRef: "Approver Entity",
            status: 0,
            actionTimestamp: "2026-07-08T07:51:39Z"
        }
    ],
    claimApprovers: [
        {
            status: ApproveStatus.PENDING,
            actionTimestamp: null,
            details: {
                roleName: "123213",
                firstName: "claim3",
                lastName: "app3",
                email: "ca3@ca.com",
                userProfileName: "Approver Entity"
            }
        },
        {
            status: ApproveStatus.PENDING,
            actionTimestamp: null,
            details: {
                roleName: "123213",
                firstName: "claim2",
                lastName: "app2",
                email: "ca2@ca.com",
                userProfileName: "Approver Entity"
            }
        },
        {
            status: ApproveStatus.PENDING,
            actionTimestamp: null,
            details: {
                roleName: "1232131",
                firstName: "claim",
                lastName: "app",
                email: "ca@ca.com",
                userProfileName: "Approver Entity"
            }
        }
    ],
    invoiceReferenceIds: [],
    contractor: {
        accountName: "a1BesuAccVitor1Con",
        userProfileName: "Contractor Company Vitor test local profile",
        wallet: "0x07f26d0b5d085ea964eed0f8dfac438f2bd1c9f4"
    },
    developer: {
        accountName: "deva_063001",
        userProfileName: "vitor developer test profile-063001",
        wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c"
    },
    commentRecords: [],
};
export const MOCK_BESU_CLAIM_STATE: BesuClaimState = {
    ...MOCK_CLAIM_PROPS,
    status: ClaimStatus.SUBMITTED,
    commentCount: 0,
    createdAt: "2026-07-08T07:51:39Z",
    updatedAt: "2026-07-08T07:51:39Z",
    comments: [],
    commentRecords: [],
};

export const MOCK_CLAIM_LIST: ClaimStateWithNestedStatus[] = [
    {
        claimState: {
            linearId: {
                id: "uuid-1",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.SUBMITTED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-2",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.DISCARDED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-3",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.APPROVED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-4",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.CA_REJECTED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [
                {
                    fullName: "ca name",
                    email: "ca@ca.com",
                    comment: "architect rejected",
                    resolved: false,
                    created: "2023-06-21T10:07:12.646Z",
                },
            ],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-5",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.APPROVED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-6",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.DELETED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [
                {
                    fullName: "ca name",
                    email: "ca@ca.com",
                    comment: "PO rejected",
                    resolved: false,
                    created: "2023-06-21T10:07:12.646Z",
                },
            ],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-7",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.APPROVED,
            props: {
                ...MOCK_CLAIM_PROPS,
                invoiceReferenceIds: MOCK_INVOICE_LIST.map(
                    (it) => it.invoiceState.linearId.id,
                ),
            },
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: MOCK_INVOICE_LIST.map((it) => ({
            invoiceStatus: it.invoiceState.status,
            paymentInfo: it.paymentInfo,
        })),
    },
    {
        claimState: {
            linearId: {
                id: "uuid-8",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.PARTIAL_CA_APPROVED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-8",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.ALL_CA_APPROVED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
    {
        claimState: {
            linearId: {
                id: "uuid-9",
            },
            createdAt: "2021-11-02T04:18:43.299Z",
            status: ClaimStatus.RESUBMITTED,
            props: MOCK_CLAIM_PROPS,
            lastChangedTime: "2021-11-02T04:18:43.299Z",
            comments: [],
        },
        invoiceStatuses: [],
    },
];


export const MOCK_BESU_CLAIM_LIST: BesuClaimListItem[] = [
    {
        createdAt: "2026-07-24T07:00:19Z",
        updatedAt: "2026-07-24T07:07:47Z",
        claimStatus: ClaimStatus.APPROVED,
        contractorWallet: "0xe4fc1b06f91bb70dcf1e3601935e14a1fa504636",
        contractorAccountName: "conAcct2",
        contractorDisplayName: "",
        invoiceCount: 3,
        claimApprovers: [
            {
                status: ApproveStatus.APPROVED,
                actionTimestamp: "2026-07-24T07:04:43Z",
                details: {
                    roleName: "ca",
                    firstName: "claim",
                    lastName: "app",
                    email: "ca@ca.com",
                    userProfileName: "Approver Entity"
                }
            }
        ],
        invoiceStatuses: [
            {
                invoiceStatus: InvoiceStatus.FINANCE_APPROVED,
                paymentInfo: {
                    paymentOrderStatus: PaymentOrderStatus.ALL_APPROVED,
                    paymentStatus: PaymentStatus.PENDING,
                    paymentOrderApprovers: [
                        {
                            status: ApproveStatus.APPROVED,
                            actionTimestamp: "2026-07-24T07:19:03Z",
                            details: {
                                roleName: "poa",
                                firstName: "po",
                                lastName: "app",
                                email: "poa@poa.com",
                                userProfileName: "Approver Entity"
                            }
                        }
                    ],
                    referenceId: "b604cbc0-da0f-8fd1-bca2-6fd6c8f79896"
                },
                referenceId: "b603508e-736c-8a6e-89cc-346fb13f948c"
            },
            {
                invoiceStatus: InvoiceStatus.FINANCE_APPROVED,
                paymentInfo: {
                    paymentOrderStatus: PaymentOrderStatus.ALL_APPROVED,
                    paymentStatus: PaymentStatus.PENDING,
                    paymentOrderApprovers: [
                        {
                            status: ApproveStatus.APPROVED,
                            actionTimestamp: "2026-07-24T08:44:55Z",
                            details: {
                                roleName: "poa",
                                firstName: "po",
                                lastName: "app",
                                email: "poa@poa.com",
                                userProfileName: "Approver Entity"
                            }
                        }
                    ],
                    referenceId: "b604c2b3-9703-8aee-bf82-3db317a39978"
                },
                referenceId: "b603d11c-b038-8e39-8964-2bcfa7a96a30"
            },
            {
                invoiceStatus: InvoiceStatus.FINANCE_APPROVED,
                paymentInfo: {
                    paymentOrderStatus: PaymentOrderStatus.ALL_APPROVED,
                    paymentStatus: PaymentStatus.PENDING,
                    paymentOrderApprovers: [
                        {
                            status: ApproveStatus.APPROVED,
                            actionTimestamp: "2026-07-27T02:17:51Z",
                            details: {
                                roleName: "poa",
                                firstName: "po",
                                lastName: "app",
                                email: "poa@poa.com",
                                userProfileName: "Approver Entity"
                            }
                        }
                    ],
                    referenceId: "b604bf9c-e648-8366-bbe4-1a61166ea941"
                },
                referenceId: "b603572b-9b50-830f-b969-025d5c4ceff3"
            }
        ],
        contractor: {
            accountName: "conAcct2",
            userProfileName: "",
            wallet: "0xe4fc1b06f91bb70dcf1e3601935e14a1fa504636"
        },
        developer: {
            accountName: "devb_071901",
            userProfileName: "",
            wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c"
        },
        commentRecords: [],
        referenceId: "b602d968-079c-89c7-9adf-48527a5aa762",
        projectReferenceId: "b601131b-2323-89f6-8e43-516f5ff0796f"
    },
    {
        createdAt: "2026-07-24T08:13:39Z",
        updatedAt: "2026-07-24T08:13:39Z",
        claimStatus: ClaimStatus.SUBMITTED,
        contractorWallet: "0x07f26d0b5d085ea964eed0f8dfac438f2bd1c9f4",
        contractorAccountName: "conb_071901",
        contractorDisplayName: "",
        invoiceCount: 0,
        claimApprovers: [
            {
                status: ApproveStatus.PENDING,
                actionTimestamp: null,
                details: {
                    roleName: "ca",
                    firstName: "claim",
                    lastName: "app",
                    email: "ca@ca.com",
                    userProfileName: "Approver Entity"
                }
            }
        ],
        invoiceStatuses: [],
        contractor: {
            accountName: "conb_071901",
            userProfileName: "",
            wallet: "0x07f26d0b5d085ea964eed0f8dfac438f2bd1c9f4"
        },
        developer: {
            accountName: "devb_071901",
            userProfileName: "",
            wallet: "0x628d684197485c054cda7d3def46e8be6b3d174c"
        },
        commentRecords: [],
        referenceId: "b6022b3b-ce9d-81e7-972f-9b350767e023",
        projectReferenceId: "b601131b-2323-89f6-8e43-516f5ff0796f"
    }
];

export const MOCK_CLAIM_HISTORY_ITEMS: HistoryItem<ClaimStatus>[] = [
    {
        status: ClaimStatus.SUBMITTED,
        timestamp: "2026-07-13T05:14:19Z",
    },
    {
        status: ClaimStatus.PARTIAL_CA_APPROVED,
        timestamp: "2026-07-13T05:16:27Z",
    },
    {
        status: ClaimStatus.ALL_CA_APPROVED,
        timestamp: "2026-07-13T05:20:51Z",
    }
];

export const MOCK_CLAIM_HISTORY: ClaimHistory = {
    referenceId: "6",
    currentStatus: ClaimStatus.ALL_CA_APPROVED,
    createdAt: "2026-07-13T05:14:19Z",
    updatedAt: "2026-07-13T05:20:51Z",
    statusHistory: MOCK_CLAIM_HISTORY_ITEMS,
};
